/**
 * Client-only "has this member seen the gameweek recap for league X, gameweek N" tracking —
 * deliberately not a DB column (see DECISIONS.md). Keyed by Firebase uid + league so a shared
 * browser with multiple Scoracle accounts doesn't cross-contaminate, and by storing only the
 * last-seen gameweek number, a new gameweek always compares greater and needs no separate
 * expiry/cleanup step. Trade-off (accepted): this does not sync across devices or browsers.
 *
 * Pure functions, no React — usable both from a hook (LeaguePage) and from a plain `.map()`
 * (HomePage rows, sidebar nav items), where hooks can't be called per-item.
 */

function storageKey(uid: string, leagueId: string): string {
  return `scoracle-gwrecap-seen:${uid}:${leagueId}`;
}

/** True once the member has seen the recap for this gameweek or a later one. Defensive
 * against a missing/corrupt stored value, matching the one existing localStorage precedent
 * in themes.ts, which also validates before trusting a stored value. */
export function hasSeenGwRecap(uid: string, leagueId: string, gameweekNumber: number): boolean {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(uid, leagueId));
  } catch {
    return false;
  }
  if (raw === null) return false;
  const lastSeen = Number(raw);
  return Number.isFinite(lastSeen) && lastSeen >= gameweekNumber;
}

/** Records that the member has now seen this gameweek's recap. Silently no-ops if localStorage
 * is unavailable (e.g. private browsing in some browsers) — worst case, the popup reappears. */
export function markGwRecapSeen(uid: string, leagueId: string, gameweekNumber: number): void {
  try {
    localStorage.setItem(storageKey(uid, leagueId), String(gameweekNumber));
  } catch {
    // Storage unavailable — nothing to do, the recap will just show again next time.
  }
}
